// Helpers for the JSON file translation flow.
//
// Strategy: when the linguist uploads a JSON document we walk the
// tree and extract every string leaf value. Each leaf becomes one
// translation request, tagged with a dotted path (e.g. "header.title"
// or "items.0.body") that records where in the document the value
// came from. On download we walk those paths in reverse to rebuild
// the tree with the approved Spanish in place of each leaf.
//
// NOTE: Reconstruction always uses plain objects (not arrays) to avoid
// creating sparse arrays when paths contain large numeric strings
// (e.g., client IDs like "987654321"). This means the downloaded JSON
// structure may differ slightly from the original if arrays were used,
// but the key-value mappings will be preserved.
//
// Path segment escaping: real-world JSON sometimes has a key that itself
// contains a literal '.' (e.g. a CMS export with sibling keys "label" and
// "label.json", the latter holding a structured/rich-text variant of the
// former). Since paths are joined with '.', an unescaped key like
// "label.json" is indistinguishable from the two-segment path
// ["label", "json"], which — once "label" resolves to a string leaf —
// causes `setPathValue` to try to assign a property on a string and either
// throw or silently drop the value. Each key is therefore escaped before
// being appended to a path, and decoded again when the path is walked:
//   encode: '~' -> '~0', then '.' -> '~1'
//   decode: '~1' -> '.', then '~0' -> '~'  (reverse order of encode)
// This mirrors RFC 6901 (JSON Pointer) escaping. Keys that contain neither
// '~' nor '.' are unchanged, so existing paths/data are unaffected.
export function encodePathSegment(segment) {
  return String(segment).replace(/~/g, '~0').replace(/\./g, '~1');
}

export function decodePathSegment(segment) {
  return segment.replace(/~1/g, '.').replace(/~0/g, '~');
}

// Recursively collect string leaves from a parsed JSON value.
// Returns [{ path: "items.0.title", value: "Welcome" }, ...].
// Skips non-string leaves, empty strings, and pure-whitespace strings
// so we never submit translations that have nothing to translate.
export function extractJsonLeaves(value, path = '') {
  if (value === null || value === undefined) return [];

  if (typeof value === 'string') {
    if (value.trim().length === 0) return [];
    return [{ path, value }];
  }

  // Numbers, booleans etc. are passed through unchanged on download
  // so we don't need to translate them.
  if (typeof value !== 'object') return [];

  if (Array.isArray(value)) {
    const out = [];
    value.forEach((item, idx) => {
      const childPath = path ? `${path}.${idx}` : String(idx);
      out.push(...extractJsonLeaves(item, childPath));
    });
    return out;
  }

  // Plain object.
  const out = [];
  Object.keys(value).forEach((key) => {
    const encodedKey = encodePathSegment(key);
    const childPath = path ? `${path}.${encodedKey}` : encodedKey;
    out.push(...extractJsonLeaves(value[key], childPath));
  });
  return out;
}

// Set a value in a nested object structure given a dotted path.
// Always uses objects (never arrays) to avoid creating sparse arrays
// when numeric path segments represent object keys (e.g., client IDs)
// rather than true array indices. Mutates `target` and creates
// intermediate containers as needed.
export function setPathValue(target, path, value) {
  if (!path) return;
  const segments = path.split('.').map(decodePathSegment);
  let cursor = target;

  for (let i = 0; i < segments.length - 1; i += 1) {
    const seg = segments[i];
    // Always use string keys - treat all segments as object keys
    // This avoids creating sparse arrays when paths contain numeric
    // strings like client IDs (e.g., "clients.987654321.name")
    if (cursor[seg] === undefined || cursor[seg] === null) {
      cursor[seg] = {};
    }
    cursor = cursor[seg];
  }

  const last = segments[segments.length - 1];
  cursor[last] = value;
}

// Build the JSON tree from a flat array of leaf rows. Each row needs
// `source_path` (string) and `value` (the Spanish text). Returns the
// reconstructed object at the root. Always uses objects to avoid
// sparse array issues with numeric path segments.
export function buildJsonFromLeaves(leaves) {
  const root = {};

  leaves.forEach((leaf) => {
    if (!leaf.source_path) return;
    setPathValue(root, leaf.source_path, leaf.value);
  });

  return root;
}

// Clone a parsed JSON document and overlay string leaf values by dotted path.
export function applyLeafValues(parsedJson, leaves) {
  const clone = JSON.parse(JSON.stringify(parsedJson));
  leaves.forEach((leaf) => {
    if (leaf.path) setPathValue(clone, leaf.path, leaf.value);
  });
  return clone;
}

// Detect whether a batch came from a JSON upload. The backend stores
// `source_format` on the batch record but older batches won't have it
// so we fall back to checking the file extension.
export function isJsonBatch(batch) {
  if (!batch) return false;
  const fmt = (batch.source_format || '').toLowerCase();
  if (fmt === 'json') return true;
  if (fmt === 'xlsx' || fmt === 'xls') return false;
  const name = (batch.file_name || '').toLowerCase();
  return name.endsWith('.json');
}
